import clsx from 'clsx'
import { useState } from 'react'
import toast from 'react-hot-toast'

import { FvButton, FvLinkButton } from '@fv/client-components'
import type { LoadDocument } from '@fv/client-types'

import { CarrierTooltip } from '@/components/CarrierTooltip'
import { CarrierModal } from '@/components/shared/CarrierModal'
import { apiUri, documentTypes, supportMessage } from '@/constants'
import type { Opportunity } from '@/types/Opportunity'

import { useOppListingActions } from '../opportunities/OppListingContextProvider'
import { useDeclineOpportunity } from './hooks/useDeclineOpportunity'
import { useUndeclineOpportunity } from './hooks/useUndeclineOpportunity'
import { MultiStopTrackingList } from './MultiStopTrackingList'
import { SelectedPriceDisplay } from './SelectedPriceDisplay'
import { Share } from './Share'

type Props = {
  opportunity: Opportunity
  onToggleQuoteForm?: () => void
  isExpired?: boolean
  hidePricing?: boolean
}
export const CardActions = ({
  opportunity,
  onToggleQuoteForm,
  isExpired,
  hidePricing = false,
}: Props) => {
  const {
    bol,
    cheapestQuoteAmount,
    cheapestQuoteCurrency,
    documents,
    isArchived,
    isLiveLoad,
    isLowest,
    loadId,
    isFreightCollect,
    status,
    showPricingIndicator,
  } = opportunity

  const [showShare, setShowShare] = useState(false)
  const decline = useDeclineOpportunity(status)
  const undecline = useUndeclineOpportunity()
  const { setOppPanelOpen, setOppPanelTab } = useOppListingActions()

  const declineQuoteRequest = () => {
    if (decline.isPending) return
    if (window.confirm('Are you sure you want to decline this opportunity?')) {
      decline.mutateAsync({ loadId }).catch(() => {
        toast.error(`Unable to decline opportunity, ${supportMessage}`)
      })
    }
  }

  const undeclineQuoteRequest = () => {
    if (undecline.isPending) return
    undecline.mutateAsync({ loadId }).catch(() => {
      toast.error(
        `Unable to move back to open opportunities, ${supportMessage}`,
      )
    })
  }

  const notLost = status !== 'lost'
  const showComparison = showPricingIndicator !== false && notLost
  const canViewBOL = status === 'awarded' || status === 'confirmed'
  const canDecline = status === 'open' || status === 'offered'
  const canShare = status !== 'declined' && status !== 'lost'
  const bols = documents?.filter(d => d.type === 'bol') ?? []
  const fvBol =
    bols.find(b => b.source === 'shipper') ??
    bols.find(b => b.source === 'system')

  const allowedDocs = [
    'bol',
    'proof-of-delivery',
    'signed-bol',
    'invoice',
    'other',
  ]
  const allowedSystemDocs = ['rate-confirmation']
  const visibleDocs = documents?.filter(
    d =>
      d.source === 'carrier' ||
      (allowedDocs.includes(d.type) && d.source !== 'system') ||
      (allowedSystemDocs.includes(d.type) && d.source === 'system'),
  )

  const showLoadStatus =
    status === 'open' ||
    status === 'declined' ||
    status === 'offered' ||
    status === 'lost'
  const showSelectedPrice =
    !hidePricing && !!cheapestQuoteAmount && !isFreightCollect

  return (
    <div
      className={clsx('shipment-item-ancillary-actions', {
        '!order-2 mb-0 mt-4': status === 'open' || status === 'declined',
      })}
    >
      <ul className="divide flex divide-x divide-fv-gray w-full [&>*]:px-3 [&>*:first-child]:pl-0">
        {showSelectedPrice && (
          <li>
            <SelectedPriceDisplay
              amount={cheapestQuoteAmount}
              currency={cheapestQuoteCurrency}
              isLowest={isLowest}
              showComparison={showComparison}
            />
          </li>
        )}

        <li>
          <FvButton
            aria-label="View messages"
            icon="comment-alt-lines"
            onClick={() => {
              setOppPanelTab('messages')
              setOppPanelOpen(true)
            }}
          ></FvButton>
        </li>

        {canShare && (
          <li>
            <FvButton
              onClick={() => setShowShare(true)}
              icon="share"
              buttonTextClass="b1200:hidden"
            >
              Share
            </FvButton>
          </li>
        )}

        {canDecline && (
          <li>
            <FvButton
              onClick={declineQuoteRequest}
              icon={decline.isPending ? 'spinner' : 'ban'}
            >
              <span>Decline</span>
            </FvButton>
          </li>
        )}

        {status === 'declined' && (
          <li>
            <CarrierTooltip label="Move back to open queue">
              <FvButton
                onClick={undeclineQuoteRequest}
                disabled={isExpired}
                icon={undecline.isPending ? 'spinner' : 'trash-restore-alt'}
              >
                <span>Move back</span>
              </FvButton>
            </CarrierTooltip>
          </li>
        )}

        {(fvBol || bol?.status === 'pending') && canViewBOL && (
          <li>
            <FvLinkButton
              target="_bol"
              href={fvBol?.url}
              icon={bol?.status === 'pending' ? 'spinner' : 'file-alt'}
              buttonTextClass="b1200:hidden"
            >
              BOL
            </FvLinkButton>
          </li>
        )}

        {status === 'confirmed' && (
          <li className="flex items-center">
            <FvButton
              onClick={() => {
                setOppPanelTab('documents')
                setOppPanelOpen(true)
              }}
              icon="cloud-upload"
            >
              <span>Docs</span>
            </FvButton>

            {visibleDocs?.length > 0 && (
              <CarrierTooltip
                label={`Currently uploaded > ${composeDocumentNames(
                  visibleDocs,
                )}`}
              >
                <FvButton className="standard-link standard-link--secondary badge">
                  {visibleDocs.length}
                </FvButton>
              </CarrierTooltip>
            )}
          </li>
        )}
        {canViewBOL && (
          <li>
            <CarrierTooltip label="Print details">
              <FvLinkButton
                aria-label="Print shipment details"
                target="_shipment-details"
                href={`${apiUri}/documents/${loadId}/shipment-details`}
                icon="print"
                buttonTextClass="b1200:hidden"
              ></FvLinkButton>
            </CarrierTooltip>
          </li>
        )}
      </ul>
      {(showLoadStatus || onToggleQuoteForm) && (
        <div className="ml-auto flex">
          <span className="ll-qo-text">
            {isLiveLoad ? 'Live load' : 'Quote only'}
          </span>

          {onToggleQuoteForm && (
            <FvButton
              className="btn ml-4"
              onClick={onToggleQuoteForm}
              type="button"
              icon="list"
              theme="default"
            >
              <span>Quote</span>
            </FvButton>
          )}
        </div>
      )}
      {status === 'confirmed' && !isArchived && (
        <MultiStopTrackingList opportunity={opportunity} />
      )}
      {showShare && (
        <CarrierModal onClose={() => setShowShare(false)}>
          <Share loadId={loadId} onClose={() => setShowShare(false)} />
        </CarrierModal>
      )}
    </div>
  )
}

function composeDocumentNames(docs: LoadDocument[] = []) {
  const docTypes = docs.reduce((types, doc) => {
    const name = documentTypes.find(t => t.value === doc.type)?.text
    return !name || types.includes(name) ? types : types.concat(name)
  }, [])

  return docTypes.join(' / ')
}
